import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "@/constants/testIds";

const items = [
  {
    code: "SCE-HR+",
    name: "Anti Moisture Powder",
    tagline: "Neutralises moisture. Instantly.",
    desc: "A specialised moisture-absorbing additive designed to eliminate porosity and surface defects in recycled plastic processing. Acts instantly to neutralise moisture content in raw materials.",
    features: ["Removes silver streaks & pinholes", "Improves surface finish", "Instant reactivity"],
    applications: ["Reprocessed granules", "Plastic moulding", "Carry bag manufacturing"],
    img: "/products/sce-hr.jpg",
  },
  {
    code: "SCE-DM",
    name: "Desiccant Masterbatch",
    tagline: "A moisture scavenger in granule form.",
    desc: "Aggressively absorbs humidity during extrusion and injection moulding, allowing higher usage of recycled materials without compromising strength.",
    features: ["Smooth surface texture", "Higher recycled content", "Boosts production efficiency"],
    applications: ["Blown films", "Garbage bags", "Injection moulding", "Sheets"],
    img: "/products/sce-dm.jpg",
  },
  {
    code: "SCE-QL",
    name: "Quick Lime Powder",
    tagline: "Industrial CaO, >93% purity.",
    desc: "High-purity industrial grade Quick Lime (CaO) engineered for superior chemical reactivity and versatility. Produced from premium limestone.",
    features: [">93% Calcium content", "Low moisture", "Rapid reactivity"],
    applications: ["Desiccant masterbatch", "Recycled plastic granules", "Sugar refining", "Chemical processing"],
    img: "/products/sce-ql.jpg",
  },
  {
    code: "SCE-CF",
    name: "Calcium Filler",
    tagline: "Reduce cost. Elevate structure.",
    desc: "A premium calcium carbonate-based filler engineered to reduce material costs while enhancing the physical properties of your end product.",
    features: ["High whiteness", "Excellent dispersion", "Improves dimensional stability"],
    applications: ["PVC pipes", "Masterbatches", "Paints", "Rubber", "Plastic compounding"],
    img: "/products/sce-cf.jpg",
  },
  {
    code: "SCE-CMB",
    name: "Colour Masterbatch",
    tagline: "Vibrance you can dose.",
    desc: "High-concentration colour concentrates that deliver vibrant, consistent hues for all plastic applications. Formulated with superior pigments for excellent dispersion and light fastness.",
    features: ["Uniform colour consistency", "High thermal stability", "Cost-effective dosage"],
    applications: ["Injection moulding", "Blow moulding", "Films", "Household plastics"],
    img: "/products/sce-cmb.jpg",
  },
];

const Products = () => {
  return (
    <section
      id="products"
      data-testid={PRODUCTS.section}
      className="relative bg-[#0A2A1E] text-[#FAF9F6] py-24 lg:py-40 px-5 lg:px-10 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-8 flex-wrap mb-16 lg:mb-24">
          <div>
            <div className="section-rule overline text-[#C6F035] mb-6">
              The Catalogue · 2026
            </div>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em] max-w-3xl">
              Five products. <br />
              <span className="font-display-italic text-[#C6F035]">
                Countless
              </span>{" "}
              factories.
            </h2>
          </div>
          <p className="text-[#FAF9F6]/70 max-w-sm text-base md:text-lg leading-relaxed">
            A tight, opinionated portfolio built to serve every stage of the
            plastic processing supply chain — from raw material to end product.
          </p>
        </div>

        <div className="space-y-28 lg:space-y-40">
          {items.map((p, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.div
                key={p.code}
                data-testid={PRODUCTS.item(p.code)}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-6 lg:gap-12 items-center"
              >
                <div className={`col-span-12 lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
                  <div className="relative overflow-hidden hover-scale clip-frame border border-[#C6F035]/20">
                    <div className="aspect-[4/3] w-full relative bg-black">
                      <img
                        src={p.img}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Spotlight */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background:
                            "radial-gradient(circle at 65% 45%, transparent 0%, rgba(10,42,30,0.55) 75%)",
                        }}
                      />
                      <div className="absolute top-4 left-4 overline text-[#C6F035]">
                        {String(i + 1).padStart(2, "0")} / 05
                      </div>
                      <div className="absolute bottom-4 right-4 overline text-[#FAF9F6]/80">
                        {p.code}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`col-span-12 lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
                  <div className="overline text-[#C6F035] mb-4">{p.code}</div>
                  <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                    {p.name}
                  </h3>
                  <p className="font-display-italic text-[#C6F035] text-2xl md:text-3xl mt-3">
                    {p.tagline}
                  </p>
                  <p className="text-[#FAF9F6]/75 mt-6 text-base md:text-lg leading-relaxed">
                    {p.desc}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-10">
                    <div>
                      <div className="overline text-[#FAF9F6]/50 mb-3">
                        Key Features
                      </div>
                      <ul className="space-y-2">
                        {p.features.map((f) => (
                          <li
                            key={f}
                            className="text-sm text-[#FAF9F6] flex items-start gap-2"
                          >
                            <span className="mt-1.5 w-1.5 h-1.5 bg-[#C6F035] shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="overline text-[#FAF9F6]/50 mb-3">
                        Applications
                      </div>
                      <ul className="space-y-2">
                        {p.applications.map((a) => (
                          <li
                            key={a}
                            className="text-sm text-[#FAF9F6] flex items-start gap-2"
                          >
                            <span className="mt-1.5 w-1.5 h-1.5 bg-[#FAF9F6]/60 shrink-0" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <a
                    href="#contact"
                    className="link-wipe inline-flex items-center gap-2 mt-10 text-[#C6F035] text-sm font-semibold tracking-[0.14em] uppercase"
                  >
                    Request Datasheet <ArrowUpRight size={14} />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Products;
